import { authClient } from '@aimfora/auth/client'
import { m } from '@aimfora/i18n'
import { Button, Notice, toast } from '@aimfora/ui'
import { usePostHog } from '@posthog/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useRouter } from '@tanstack/react-router'
import { AuthCard, textLinkClass } from '~/components/auth-card'
import { authErrorMessage } from '~/lib/auth-errors'
import { resetPostHog } from '~/lib/consent'
import { roleLabel } from '~/lib/roles'

export const Route = createFileRoute('/invite/$id')({
  head: () => ({ meta: [{ title: `${m.invite_title()} – ${m.app_name()}` }] }),
  component: InvitePage,
})

function InvitePage() {
  const { id } = Route.useParams()
  const { user } = Route.useRouteContext()
  const here = `/invite/${id}`

  if (!user) {
    return (
      <AuthCard title={m.invite_title()}>
        <p className="mb-5 text-ink-soft text-sm">{m.invite_login_required_generic()}</p>
        <div className="flex flex-col gap-2">
          <Button asChild>
            <Link to="/login" search={{ redirect: here }}>
              {m.auth_login_title()}
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/signup" search={{ redirect: here }}>
              {m.auth_signup_title()}
            </Link>
          </Button>
        </div>
      </AuthCard>
    )
  }
  return <InviteForUser id={id} currentEmail={user.email} />
}

function InviteForUser({ id, currentEmail }: { id: string; currentEmail: string }) {
  const router = useRouter()
  const posthog = usePostHog()

  const invitation = useQuery({
    queryKey: ['invitation', id],
    retry: false,
    queryFn: async () => {
      const { data, error } = await authClient.organization.getInvitation({ query: { id } })
      if (error) throw error
      return data
    },
  })

  const respond = useMutation({
    mutationFn: async (action: 'accept' | 'decline') => {
      const { error } =
        action === 'accept'
          ? await authClient.organization.acceptInvitation({ invitationId: id })
          : await authClient.organization.rejectInvitation({ invitationId: id })
      if (error) throw error
      return action
    },
    onSuccess: async (action, _vars) => {
      await router.invalidate()
      if (action === 'accept') {
        toast.success(m.invite_accepted({ organization: invitation.data?.organizationName ?? '' }))
        await router.navigate({ to: '/app' })
      } else {
        toast(m.invite_declined())
        await router.navigate({ to: '/app' })
      }
    },
  })

  const signOutAndBack = async () => {
    const { error } = await authClient.signOut()
    if (!error) resetPostHog(posthog)
    await router.invalidate()
    await router.navigate({ to: '/login', search: { redirect: `/invite/${id}` } })
  }

  if (invitation.isPending) {
    return (
      <AuthCard title={m.invite_title()}>
        <p role="status" className="text-ink-soft text-sm">
          {m.common_loading()}
        </p>
      </AuthCard>
    )
  }

  if (invitation.isError) {
    const wrongAccount =
      (invitation.error as { code?: string }).code === 'YOU_ARE_NOT_THE_RECIPIENT_OF_THE_INVITATION'
    return (
      <AuthCard
        title={m.invite_title()}
        footer={
          <Link className={textLinkClass} to="/app">
            {m.forbidden_back()}
          </Link>
        }
      >
        <Notice tone="attention" role="alert">
          {wrongAccount
            ? m.invite_wrong_account_generic({ current: currentEmail })
            : m.invite_not_found()}
        </Notice>
        {wrongAccount && (
          <Button variant="outline" className="mt-4 w-full" onClick={() => void signOutAndBack()}>
            {m.auth_logout()}
          </Button>
        )}
      </AuthCard>
    )
  }

  const data = invitation.data
  const pending = data.status === 'pending'
  return (
    <AuthCard title={m.invite_title()}>
      <div className="space-y-4">
        <p className="text-sm">
          {m.invite_summary({
            inviter: data.inviterEmail,
            organization: data.organizationName,
            role: roleLabel(data.role).toLowerCase(),
          })}
        </p>
        {!pending && <Notice>{m.invite_status_accepted()}</Notice>}
        {respond.isError && (
          <Notice tone="attention" role="alert">
            {authErrorMessage(respond.error as { code?: string; status?: number })}
          </Notice>
        )}
        {pending && (
          <div className="flex flex-col gap-2">
            <Button disabled={respond.isPending} onClick={() => respond.mutate('accept')}>
              {m.invite_accept()}
            </Button>
            <Button
              variant="ghost"
              disabled={respond.isPending}
              onClick={() => respond.mutate('decline')}
            >
              {m.invite_decline()}
            </Button>
          </div>
        )}
      </div>
    </AuthCard>
  )
}
